import { Component, OnInit, ViewChild, inject } from "@angular/core";

import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { Command } from "@tylertech/commands-module";
import { isExecutionResult, isHttpResponseError } from "@tylertech/core-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { AdvancedComponentsDialogService } from "@tylertech/advanced-components-module";
import { MetadataService } from "@tylertech/metadata-module";
import
{
    ClassMetadata,
    ExecutionResult,
    GetNoteObjectViewModel,
    IAttachment,
    IInfoTable,
    IInfoTableCriteria,
    InfoTableSaveCriteria,
    MetadataVerification,
} from "@tylertech/tyler-api-types";

import { InfoTableService } from "../../services";
import { Utilities } from "@tylertech/shared-module";
import { DocumentService } from "@tylertech/attachments-module";
import { NgForm } from "@angular/forms";

export interface EditDialogData
{
    readonly allowAttachments: boolean;
    readonly preventSaveAndNew: boolean;
    readonly allowNotes: boolean;
    readonly canDelete: boolean;
    readonly criteria: IInfoTableCriteria;
    readonly entityName: string;
    readonly hideNoteSubject: boolean;
    readonly infoTable: IInfoTable;
    readonly viewOnly: boolean;
}

const enum SaveType
{
    Default,        // 0
    SaveAndNew,     // 1
    SaveAndNext,    // 2
    Add,            // 3
}

@Component({
    selector: "edit-dialog-component",
    templateUrl: "./editDialog.tmpl.html",
    standalone: false
})
export class EditDialogComponent implements OnInit
{
    private readonly dialogRef = inject<MatDialogRef<EditDialogComponent, boolean>>(MatDialogRef);
    private readonly data = inject<EditDialogData>(MAT_DIALOG_DATA);
    private readonly $documentApiService = inject(DocumentService);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $advancedComponentsDialogService = inject(AdvancedComponentsDialogService);
    private readonly $metadataService = inject(MetadataService);
    private readonly $infoTableService = inject(InfoTableService);

    addMode: boolean;
    additionalCommands: Array<Command> = [];
    attachments: Array<IAttachment> = [];
    entity: Partial<Record<"Id", string>>;
    @ViewChild("form") form: NgForm;
    isLoaded: boolean;
    metadata: ClassMetadata;
    noteAttachmentEntity: GetNoteObjectViewModel;
    tcmModel: unknown;
    private _viewOnly: boolean;
    hasTCM: boolean;
    private _disableAttachments: boolean;
    private _disableNotes: boolean;

    private get entityName(): string { return this.data.entityName; }
    private get infoTable(): IInfoTable { return this.data.infoTable; }

    get disableAttachments(): boolean { return this._disableAttachments || (this._disableAttachments = !this.data.allowAttachments); }
    get disableNotes(): boolean { return this._disableNotes || (this._disableNotes = !this.data.allowNotes); }
    get canDelete(): boolean { return !this.viewOnly && this.data.canDelete; }
    get canSave(): boolean { return !this.form.invalid && !this.viewOnly; }
    get canSaveAndNew(): boolean
    {
        return this.addMode
            && !this.viewOnly
            && (this.infoTable && !this.infoTable?.PreventSaveAndNew)
            || (!this.infoTable && this.data && !this.data?.preventSaveAndNew);
    }

    get canSaveAndNext(): boolean
    {
        return !this.addMode && this.hasNext && !this.viewOnly;
    }

    get canMoveNext(): boolean
    {
        return this.viewOnly && this.hasNext;
    }

    get criteria(): IInfoTableCriteria { return this.data.criteria; }

    get hasNext(): boolean
    {
        return this.infoTable.Rows.length > 1;
    }

    get hideNoteSubject(): boolean { return this.data.hideNoteSubject; }
    get showVertMenu(): boolean
    {
        return (!this.disableAttachments || !this.disableNotes) && (!this.addMode || this.canSave);
    }

    get title(): string { return this.addMode ? "Add Item" : this.viewOnly ? "View Item" : "Edit Item"; }
    get viewOnly(): boolean { return this._viewOnly || this.data.viewOnly; }
    set viewOnly(value: boolean) { this._viewOnly = value; }

    ngOnInit(): void
    {
        this.fetchEntity(false);

        this.$documentApiService.HasTcm()
            .then(
                (executionResult: ExecutionResult<boolean>) =>
                {
                    this.hasTCM = executionResult.Result;
                },
                (error: unknown) =>
                {
                    console.error("Unknown error", error);
                    this._disableAttachments = true;
                });

        this.$infoTableService.GetMetadata(this.criteria.ProviderId)
            .then(result =>
            {
                this.metadata = result;
                if (!this.metadata || !this.viewOnly)
                {
                    return;
                }

                this.metadata.Properties.forEach(prop =>
                {
                    prop.Disabled = true;
                });
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });

        this.noteAttachmentEntity = {
            Id: undefined,
            Type: this.entityName,
        };
    }

    onBeforeDocumentAttach(): void
    {
        this.$commonDialogsService.showConfirmationMessage(
            "You must save the item before attaching documents or adding notes.",
            "Save Item",
            "Save Item")
            .then(
                () =>
                {
                    if (this.addMode)
                    {
                        this.internalSave(SaveType.Add);
                    }
                    else
                    {

                        this.$advancedComponentsDialogService.showAddAttachment(
                            {
                                attachments: this.attachments,
                                entity: this.noteAttachmentEntity,
                                hasTCM: this.hasTCM
                            })
                            .then(attachments =>
                            {
                                if (!attachments)
                                {
                                    return;
                                }

                                this.attachments = attachments;
                            });
                    }
                },
                _r =>
                {
                    /* NO_OP */
                });
    }

    cancel(): void
    {
        this.dialogRef.close();
    }

    closeAndDelete(): void
    {
        this.dialogRef.close(true);
    }

    private fetchEntity(isLoadNext: boolean): Promise<void>
    {
        if (!Utilities.IsNullOrUndefinedOrEmpty(this.criteria.Id) && this.criteria.ProviderId)
        {
            this.addMode = false;
            return this.$infoTableService.GetEntityForEdit(this.criteria)
                .then(result =>
                {
                    this.entity = result;
                    this.noteAttachmentEntity.Id = Utilities.IsNullOrUndefined(result.Id) ? result.ID : result.Id;
                    if (isLoadNext)
                    {
                        this.isLoaded = true;
                    }
                });
        }
        else
        {
            this.entity = { Id: this.criteria.Id };
            this.addMode = Utilities.IsNullOrUndefined(this.criteria.Id);
            if (!this.addMode)
            {
                this.noteAttachmentEntity = {
                    Id: this.criteria.Id,
                    Type: this.entityName,
                };
            }
            this.isLoaded = true;
            return Promise.resolve();
        }
    }

    private loadNext(): void
    {
        const current = this.infoTable.Rows.find(r => r.Id === this.criteria.Id);
        const index = this.infoTable.Rows.indexOf(current);
        if (index >= 0 && index <= this.infoTable.Rows.length - 1)
        {
            if (index === this.infoTable.Rows.length - 1)
            {
                this.criteria.Id = this.infoTable.Rows[0].Id;
                this.viewOnly = this.infoTable.Rows[0].Disabled;
            }
            else
            {
                this.criteria.Id = this.infoTable.Rows[index + 1].Id;
                this.viewOnly = this.infoTable.Rows[index + 1].Disabled;
            }
            this.fetchEntity(true);
        }
    }

    private internalSave(saveType: SaveType): void
    {
        if (!this.form.dirty)
        {
            this.afterSave(saveType);
            return;
        }

        this.isLoaded = false;
        this.$infoTableService.SaveEntity(
            {
                Id: this.entity.Id,
                Entity: this.entity,
                ProviderId: this.criteria.ProviderId,
                ParentId: Utilities.IsNullOrUndefined(this.criteria.Id) ? this.criteria.ParentId : this.criteria.Id,
                Result: undefined,
            })
            .then(
                result =>
                {
                    if (result.Result)
                    {
                        this.afterSave(saveType, result);
                    }
                },
                (result: unknown) =>
                {
                    let msg = "";
                    if (!isHttpResponseError(result))
                    {
                        msg = "An Error Occurred.";
                    }
                    else if (result.error.AdditionalInfo)
                    {
                        const verification = <MetadataVerification>JSON.parse(result.error.AdditionalInfo);
                        this.$metadataService.ShowValidationDialog(verification);
                        return;
                    }

                    else if (result.error.Message)
                    {
                        msg = result.error.Message;
                    }
                    else if (isExecutionResult(result) && Array.isArray(result.Result) && result.Result?.length > 0)
                    {
                        for (let i = 0, l = result.Result.length; i < l; i++)
                        {
                            msg += result.Result[i] + "<br/>";
                        }
                    }
                    else
                    {
                        msg = "An Error Occurred.";
                    }

                    this.$commonDialogsService.showAlertHtmlMessage(
                        `Failed to save due to the following: <br/>${msg}`, "Save Failed!");
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private afterSave(pType: SaveType, result?: InfoTableSaveCriteria): void
    {
        switch (pType)
        {
            case SaveType.Default:
                this.dialogRef.close();
                break;
            case SaveType.SaveAndNew:
                this.addMode = true;
                this.entity = {};
                this.isLoaded = true;
                break;
            case SaveType.SaveAndNext:
                this.loadNext();
                break;
            case SaveType.Add:
                this.entity = result.Entity;
                this.noteAttachmentEntity.Id = Utilities.IsNullOrUndefined(result.Entity.Id)
                    ? result.Entity.ID
                    : result.Entity.Id;
                this.isLoaded = true;
                this.onBeforeDocumentAttach();
                break;
        }
    }

    saveAndNew(): void
    {
        this.internalSave(SaveType.SaveAndNew);
    }

    saveAndNext(): void
    {
        this.internalSave(SaveType.SaveAndNext);
    }

    save(): void
    {
        this.internalSave(SaveType.Default);
    }

    updateAttachments(attachments: Array<IAttachment>): void
    {
        this.attachments = !attachments ? [] : attachments;
    }
}
